import React, { useEffect, useRef, useState } from 'react';
import supabase from '../config/supabase';

const COLORS = {
  primaryDark: '#9D7F66',
  dark: '#5A4A42',
  medium: '#8B7B73',
  muted: '#A89890',
};

const PULSE_KEYFRAMES = `
@keyframes dashboardMsgPulse {
  0%   { box-shadow: 0 0 0 0 rgba(230, 100, 30, 0.55); background-color: #FFF3E6; }
  40%  { box-shadow: 0 0 0 8px rgba(230, 100, 30, 0); background-color: #FFE4C4; }
  100% { box-shadow: 0 0 0 0 rgba(230, 100, 30, 0); background-color: transparent; }
}
.dm-pulse {
  animation: dashboardMsgPulse 0.85s ease-out 3;
}
`;

let styleInjected = false;
function injectStyles() {
  if (styleInjected || typeof document === 'undefined') return;
  styleInjected = true;
  const el = document.createElement('style');
  el.textContent = PULSE_KEYFRAMES;
  document.head.appendChild(el);
}

// ── Shared message-state helpers ────────────────────────────────────────────
// Used by the classic dashboard's message card and by the AI Command Center's
// live feed, so seen/dismiss behavior stays identical on both surfaces.

const seenMessageIds = new Set();

/** Record first_seen_at for messages that don't have it yet, then tell the
 *  sidebar / mobile nav badges to hide. Idempotent across mounts. */
export async function markMessagesSeen(messages) {
  const unseen = (messages || []).filter((m) => !m.first_seen_at && !seenMessageIds.has(m.id));
  if (unseen.length === 0) return;

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) return;
  const userId = session.user.id;

  const rows = unseen.map((m) => ({
    user_id: userId,
    message_id: m.id,
    first_seen_at: new Date().toISOString(),
  }));
  rows.forEach((r) => seenMessageIds.add(r.message_id));

  const { error: upsertErr } = await supabase
    .from('app_message_reads')
    .upsert(rows, { onConflict: 'user_id,message_id', ignoreDuplicates: true });

  // Only tell the sidebar / mobile nav badges to hide if the read state was
  // actually recorded — otherwise leave the dot in place so the user still
  // sees the hint on the next page load.
  if (!upsertErr && typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent('dashboard-messages-seen'));
    } catch (_) {}
  }
}

/** Mark a message read ("✓ Got it"). Throws on failure so callers can roll
 *  back optimistic UI. */
export async function dismissMessage(messageId) {
  const { error } = await supabase.rpc('mark_message_read', {
    p_message_id: messageId,
  });
  if (error) throw error;
}

const DashboardMessages = ({ isMobile, sectionStyle }) => {
  const [messages, setMessages] = useState(null);
  const [error, setError] = useState(null);
  const [dismissing, setDismissing] = useState({});
  const [dismissErrors, setDismissErrors] = useState({});
  const dismissSnapshotsRef = useRef({});

  useEffect(() => {
    injectStyles();
    let cancelled = false;

    async function fetchMessages() {
      try {
        const { data, error: rpcErr } = await supabase.rpc('get_active_messages_with_read_state');
        if (cancelled) return;
        if (rpcErr) {
          setError(rpcErr.message);
          return;
        }
        setMessages(data || []);
        await markMessagesSeen(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    }

    fetchMessages();
    return () => { cancelled = true; };
  }, []);

  const handleDismiss = async (messageId) => {
    if (dismissing[messageId]) return;
    setDismissing(prev => ({ ...prev, [messageId]: true }));
    setDismissErrors(prev => {
      if (!prev[messageId]) return prev;
      const next = { ...prev };
      delete next[messageId];
      return next;
    });

    const nowIso = new Date().toISOString();

    const current = (messages || []).find(m => m.id === messageId);
    if (current) {
      dismissSnapshotsRef.current[messageId] = {
        read_at: current.read_at,
        first_seen_at: current.first_seen_at,
      };
    }

    setMessages(prev => (prev || []).map(m => (
      m.id === messageId
        ? { ...m, read_at: nowIso, first_seen_at: m.first_seen_at || nowIso }
        : m
    )));

    try {
      await dismissMessage(messageId);
      delete dismissSnapshotsRef.current[messageId];
    } catch (err) {
      const snapshot = dismissSnapshotsRef.current[messageId];
      delete dismissSnapshotsRef.current[messageId];
      if (snapshot) {
        setMessages(prev => (prev || []).map(m => (
          m.id === messageId
            ? { ...m, read_at: snapshot.read_at, first_seen_at: snapshot.first_seen_at }
            : m
        )));
      }
      setDismissErrors(prev => ({
        ...prev,
        [messageId]: err.message || 'Could not dismiss message.',
      }));
    } finally {
      setDismissing(prev => {
        const next = { ...prev };
        delete next[messageId];
        return next;
      });
    }
  };

  const cardStyle = {
    marginBottom: '1rem',
    border: '1px solid #DEC9B4',
    borderRadius: '14px',
    backgroundColor: '#FCF3E8',
    padding: isMobile ? '0.8rem' : '1.1rem',
    boxShadow: '0 12px 26px rgba(90, 74, 66, 0.1)',
    ...sectionStyle,
  };

  const header = (
    <div style={{ marginBottom: messages && messages.length > 0 ? '0.65rem' : 0 }}>
      <div style={{
        fontSize: '0.74rem',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: '700',
        color: COLORS.primaryDark,
        marginBottom: '0.15rem',
      }}>
        From Countertop-app
      </div>
      <div style={{
        fontFamily: "'Montserrat', sans-serif",
        fontSize: isMobile ? '1.06rem' : '1.17rem',
        fontWeight: '700',
        color: COLORS.dark,
      }}>
        💬 Messages from Countertop-app
      </div>
    </div>
  );

  if (messages === null && !error) {
    return (
      <div style={cardStyle}>
        {header}
        <p style={{ margin: 0, fontSize: '0.94rem', color: COLORS.muted }}>Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={cardStyle}>
        {header}
        <p style={{ margin: 0, fontSize: '0.94rem', color: COLORS.medium }}>Could not load messages.</p>
      </div>
    );
  }

  const visible = messages.filter(m => !m.read_at);

  return (
    <div style={cardStyle}>
      {header}
      {visible.length === 0 ? (
        <p style={{ margin: 0, fontSize: isMobile ? '0.94rem' : '1.01rem', color: COLORS.muted }}>
          No messages right now — check back later.
        </p>
      ) : (
        <div style={{
          borderTop: '1px solid #DEC9B4',
          paddingTop: '0.65rem',
          display: 'grid',
          gap: '0.55rem',
        }}>
          {visible.map(msg => {
            const isNew = !msg.first_seen_at;
            return (
              <MessageCard
                key={msg.id}
                msg={msg}
                isNew={isNew}
                isMobile={isMobile}
                onDismiss={handleDismiss}
                isDismissing={!!dismissing[msg.id]}
                dismissError={dismissErrors[msg.id]}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const MessageCard = ({ msg, isNew, isMobile, onDismiss, isDismissing, dismissError }) => {
  const ref = useRef(null);
  const animatedRef = useRef(false);

  useEffect(() => {
    if (!isNew || animatedRef.current || !ref.current) return;
    animatedRef.current = true;
    const el = ref.current;
    el.classList.add('dm-pulse');
    const onEnd = () => el.classList.remove('dm-pulse');
    el.addEventListener('animationend', onEnd, { once: true });
    return () => el.removeEventListener('animationend', onEnd);
  }, [isNew]);

  const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div
      ref={ref}
      style={{
        background: '#FFFFFF',
        border: '1px solid #E8D8C8',
        borderRadius: '10px',
        padding: isMobile ? '0.6rem 0.75rem' : '0.7rem 0.9rem',
      }}
    >
      <div style={{
        fontFamily: "'Montserrat', sans-serif",
        fontSize: isMobile ? '1.01rem' : '1.1rem',
        fontWeight: '700',
        color: COLORS.dark,
        marginBottom: '0.3rem',
      }}>
        {msg.title}
      </div>
      {msg.body && (
        <div style={{
          fontSize: isMobile ? '0.92rem' : '0.99rem',
          color: COLORS.medium,
          whiteSpace: 'pre-wrap',
          lineHeight: '1.5',
          marginBottom: '0.35rem',
        }}>
          {msg.body}
        </div>
      )}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.5rem',
        marginTop: '0.15rem',
      }}>
        <div style={{ fontSize: '0.78rem', color: COLORS.muted }}>
          {fmt(msg.created_at)}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={() => onDismiss(msg.id)}
            disabled={isDismissing}
            aria-label="Dismiss message"
            style={{
              border: '1px solid #DEC9B4',
              backgroundColor: isDismissing ? '#F2E6D6' : '#FFFFFF',
              color: COLORS.dark,
              fontFamily: "'Montserrat', sans-serif",
              fontSize: isMobile ? '0.9rem' : '0.92rem',
              fontWeight: '600',
              padding: isMobile ? '0.4rem 0.75rem' : '0.35rem 0.8rem',
              borderRadius: '999px',
              cursor: isDismissing ? 'default' : 'pointer',
              opacity: isDismissing ? 0.7 : 1,
              minHeight: isMobile ? '34px' : 'auto',
              touchAction: 'manipulation',
              whiteSpace: 'nowrap',
            }}
          >
            {isDismissing ? 'Dismissing…' : '✓ Got it'}
          </button>
        )}
      </div>
      {dismissError && (
        <div
          role="alert"
          style={{
            marginTop: '0.4rem',
            fontSize: '0.83rem',
            color: '#A8442B',
          }}
        >
          Couldn't dismiss — please try again.
        </div>
      )}
    </div>
  );
};

export default DashboardMessages;
